// Washington University in St. Louis: public college page (#2652, batch 2),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - WashU's Common Data Set 2025-2026 (2025-2026-WashU-CDS.pdf from
//    washu.edu): C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table; C8 test policy for Fall 2027; C21 early decision
//    counts; C22 early action dates (no early action counts); G1/G5
//    2026-2027 costs.
//  - admissions.washu.edu: Application Dates & Deadlines ("Fall 2027
//    first-year applicants"), First-Year U.S. Citizen/Permanent Resident
//    Applicants (decision plans, test-optional policy, school choice,
//    coursework), and Portfolio Reviews (College of Art, College of
//    Architecture). Read in a headless browser.
//  - College Scorecard, school id 179867 (net price by family income). The
//    API's DEMO_KEY was over its rate limit, so the figures were read from
//    the Scorecard's own school page, which shows the same latest fields.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'WashU Common Data Set 2025-2026',
        url: 'https://washu.edu/app/uploads/2026/06/2025-2026-WashU-CDS.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'WashU Undergraduate Admissions, Application Dates and Deadlines',
        url: 'https://admissions.washu.edu/how-to-apply/application-dates-deadlines/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'WashU Undergraduate Admissions, First-Year U.S. Applicants',
        url: 'https://admissions.washu.edu/how-to-apply/first-year-us-applicants/',
        read: '2026-10-01',
    },
    portfolio: {
        label: 'WashU Undergraduate Admissions, Portfolio Reviews',
        url: 'https://admissions.washu.edu/how-to-apply/portfolio-reviews/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?179867-Washington-University-in-St-Louis',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'washington-university-in-st-louis',
    kbId: 'washington_university_in_st_louis',
    name: 'Washington University in St. Louis',
    shortName: 'WashU',
    aliases: ['WUSTL', 'Wash U'],
    location: 'St. Louis, MO',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (WashU brand colors); decoration only.
    branding: {
        primary: '#BA0C2F',
        secondary: '#215732',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/washington_university_in_st_louis_logo.png',
    },

    faq: {
        answer: { text: 'WashU admitted 11.9% of first-year applicants for fall 2025, 3,968 of the 33,283 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the WashU early action acceptance rate?', a: 'WashU\'s Common Data Set gives no early action counts for fall 2025, so there is no early action rate to report. It does report Early Decision I and II together: 1,333 of 4,934 applicants admitted, 27%.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'When is the WashU early action deadline?', a: 'For fall 2027, Early Action and Early Decision I applications are both due November 2. Early Action decisions come by December 23 and Early Decision I decisions by December 11.', source: 'deadlines' },
            { q: 'How hard is it to get into WashU?', a: 'For fall 2025, WashU admitted 3,968 of 33,283 first-year applicants, 11.9%. Out-of-state applicants were admitted at 12.8% (3,003 of 23,436) and international applicants at 7.3% (575 of 7,830).', source: 'cds', where: 'section C1, residency table' },
            { q: 'Does WashU require the SAT or ACT?', a: 'No. WashU is test-optional, and if you send scores you can self-report them on the Common App or Coalition App. The last test date accepted is in December, or October if you apply Early Action or Early Decision I.', source: 'firstYear' },
            { q: 'How much is WashU tuition?', a: 'Tuition for 2026-2027 is $71,310, plus $1,412 in required fees. Food and housing on campus add $24,272.', source: 'cds', where: 'section G1' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 33283,
        admitted: 3968,
        enrolled: 1961,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2017, admitted: 390 },
            { group: 'Out-of-state', applied: 23436, admitted: 3003 },
            { group: 'International', applied: 7830, admitted: 575 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'WashU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision I and II together for fall 2025, and gives no early action counts.',
        rows: [
            { plan: 'Early Decision I and II', applied: 4934, admitted: 1333 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You choose one of WashU\'s undergraduate schools when you apply: Architecture, Art, Arts & Sciences, Business or Engineering. Some ask for more:',
        rows: [
            { school: 'College of Art', requirement: 'A portfolio is required, submitted through SlideRoom.', source: 'portfolio' },
            { school: 'College of Architecture', requirement: 'A portfolio is strongly encouraged.', source: 'portfolio' },
            { school: 'Architecture, Business, Engineering', requirement: 'Calculus is strongly recommended. WashU requires no specific courses, but engineering also recommends chemistry and physics.', source: 'firstYear' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Action', deadline: 'November 2', decision: 'December 23', binding: false },
            { plan: 'Early Decision I', deadline: 'November 2', decision: 'December 11', binding: true },
            { plan: 'Early Decision II', deadline: 'January 4', decision: 'February 12', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'April 1', binding: false },
        ],
        note: 'Admission decisions and financial aid offers come together. School materials from your counselor are due November 9 (Early Action, Early Decision I) or January 11 (Early Decision II, Regular Decision). Admitted students reply by January 4 (Early Decision I), March 1 (Early Decision II) or May 1 (Early Action, Regular Decision).',
        source: 'deadlines',
        binding: 'Early Decision is a commitment to attend WashU if admitted, and you may apply Early Decision to only one college. You, your counselor and a parent or guardian sign the commitment statement. If admitted, you withdraw your other applications. If your family finds the cost not affordable after working with a financial aid counselor, you are released from the commitment. Early Action is not binding.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'WashU is test-optional: applicants with or without test scores get full consideration. If you send SAT or ACT scores, you can type them into the application; if you enroll, you send official reports. The last test date accepted is in December (October for Early Action and Early Decision I).',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 71310 },
            { item: 'Required fees', amount: 1412 },
            { item: 'Food and housing (on campus)', amount: 24272 },
            { item: 'Books and supplies', amount: 1412 },
            { item: 'Transportation', amount: 1050 },
            { item: 'Other expenses', amount: 2804 },
        ],
        total: 102260,
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 13360,
            byIncome: [
                { income: '$0 to $30,000', amount: 2578 },
                { income: '$30,001 to $48,000', amount: 589 },
                { income: '$48,001 to $75,000', amount: 7361 },
                { income: '$75,001 to $110,000', amount: 15636 },
                { income: 'Over $110,000', amount: 47130 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from WashU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
